import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Video, Radio, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Брандиране: лого и цветове на училището
const SCHOOL_LOGO = 'https://customer-assets.emergentagent.com/job_lesson-board-app/artifacts/8fx974k1_logo%20%281%29.png';
const BRAND_GREEN_DARK = '#04231c';

function loadJitsiScript(domain) {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve();
    const existing = document.querySelector('script[data-jitsi-api]');
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = `https://${domain}/external_api.js`;
    script.async = true;
    script.dataset.jitsiApi = 'true';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Видео модулът не можа да се зареди'));
    document.body.appendChild(script);
  });
}

export default function VirtualClassroom() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(true);
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/online-lessons/${lessonId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLesson(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Онлайн часът не е намерен'));
  }, [lessonId, token]);

  useEffect(() => {
    if (!lesson || !containerRef.current) return;
    let cancelled = false;

    const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'Гост';
    const displayName = user?.role === 'teacher' ? `${fullName} (Учител)` : (user?.role === 'administrator' || user?.role === 'online_administrator') ? `${fullName} (Администратор)` : fullName;
    const isModerator = user?.role === 'teacher' || user?.role === 'administrator' || user?.role === 'online_administrator';

    axios.post(`${API_URL}/jitsi/token`, { room: lesson.room_name }, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const { jwt, domain } = res.data;
        return loadJitsiScript(domain).then(() => ({ jwt, domain }));
      })
      .then(({ jwt, domain }) => {
        if (cancelled || !containerRef.current) return;
        const api = new window.JitsiMeetExternalAPI(domain, {
          roomName: lesson.room_name,
          jwt,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          userInfo: { displayName },
          configOverwrite: {
            subject: lesson.title,
            defaultLanguage: 'bg',
            prejoinConfig: { enabled: true },
            startWithAudioMuted: user?.role === 'student',
            disableInviteFunctions: true,
            disableDeepLinking: true,
            channelLastN: -1,
            startTileView: user?.role === 'teacher',
            disableTileView: false,
            defaultLogoUrl: SCHOOL_LOGO,
            hideConferenceSubject: false,
            localRecording: { disable: false, notifyAllParticipants: true },
            recordingService: { enabled: true },
            toolbarButtons: [
              'microphone', 'camera', 'desktop', 'chat', 'raisehand',
              'tileview', 'fullscreen', 'participants-pane', 'settings',
              ...(isModerator ? ['security', 'mute-everyone', 'mute-video-everyone', 'recording'] : []),
              'hangup',
            ],
          },
          interfaceConfigOverwrite: {
            SHOW_POWERED_BY: false,
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            JITSI_WATERMARK_LINK: '',
            BRAND_WATERMARK_LINK: '',
            DEFAULT_LOGO_URL: SCHOOL_LOGO,
            DEFAULT_WELCOME_PAGE_LOGO_URL: SCHOOL_LOGO,
            APP_NAME: 'БНУ Азбукарче',
            NATIVE_APP_NAME: 'БНУ Азбукарче',
            PROVIDER_NAME: 'БНУ Азбукарче',
            DEFAULT_BACKGROUND: BRAND_GREEN_DARK,
            DEFAULT_REMOTE_DISPLAY_NAME: 'Участник',
            MOBILE_APP_PROMO: false,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
            TOOLBAR_ALWAYS_VISIBLE: true,
            TILE_VIEW_MAX_COLUMNS: 5,
          },
        });
        apiRef.current = api;
        api.addListener('videoConferenceJoined', () => setJoining(false));
        api.addListener('readyToClose', () => navigate(-1));
        setJoining(false);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));

    return () => {
      cancelled = true;
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
  }, [lesson, user, navigate]);

  const isLive = lesson && (() => {
    const start = new Date(lesson.scheduled_at).getTime();
    return Date.now() >= start && Date.now() <= start + lesson.duration_minutes * 60000;
  })();

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8EDE2] flex flex-col items-center justify-center px-4">
        <Video className="h-12 w-12 text-slate-300 mb-4" />
        <p className="text-slate-700 font-semibold text-center">{error}</p>
        <Button onClick={() => navigate(-1)} className="mt-5 bg-[#00966E] hover:bg-[#007a59] text-white rounded-full">
          <ArrowLeft className="h-4 w-4 mr-2" /> Назад
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="virtual-classroom-page" className="h-screen flex flex-col bg-slate-900">
      <header className="flex items-center justify-between px-3 sm:px-6 h-14 bg-[#00966E] shrink-0">
        <button
          data-testid="classroom-back-btn"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Назад</span>
        </button>
        <div className="flex items-center gap-2 min-w-0 px-2">
          <Video className="h-4 w-4 text-white/80 shrink-0" />
          <h1 className="text-white text-sm sm:text-base font-semibold truncate">
            {lesson?.title || 'Виртуална класна стая'}
          </h1>
          {lesson && <span className="hidden sm:inline text-white/70 text-xs">· {lesson.class_name}</span>}
        </div>
        <div className="shrink-0">
          {isLive && (
            <Badge className="bg-red-500 hover:bg-red-500 text-white animate-pulse">
              <Radio className="h-3 w-3 mr-1" /> НА ЖИВО
            </Badge>
          )}
        </div>
      </header>

      <main className="flex-1 relative">
        {joining && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80 z-0">
            <Loader2 className="h-9 w-9 animate-spin" />
            <p className="text-sm">Свързване с виртуалната стая…</p>
          </div>
        )}
        <div ref={containerRef} data-testid="jitsi-container" className="absolute inset-0" />
      </main>
    </div>
  );
}
